import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useStore } from '../../context/StoreContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiUsers } = FiIcons;

export const Guests = () => {
  const navigate = useNavigate();
  const { currentEvent } = useStore();
  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (currentEvent && currentEvent.event_id) {
      api.getGuests(currentEvent.event_id).then(setGuests);
    }
  }, [currentEvent]);

  const filteredGuests = guests.filter(g => {
    const matchesSearch = 
      g.contact.first_name?.toLowerCase().includes(search.toLowerCase()) || 
      g.contact.last_name?.toLowerCase().includes(search.toLowerCase()) || 
      g.contact.email?.toLowerCase().includes(search.toLowerCase());
      
    if (!matchesSearch) return false;
    
    if (filter === 'Checked In') return g.checked_in;
    if (filter === 'Not Arrived') return !g.checked_in;
    if (filter === 'Walk-Ins') return g.walk_in;
    if (filter === 'Prospective') return g.relationship?.relationship_type === 'Prospective Couple';
    if (filter === 'Booked') return g.relationship?.relationship_type === 'Booked Couple';
    if (filter === 'Never Toured') return g.relationship?.tour_status === 'Never Toured';
    if (filter === 'Previously Toured') return g.relationship?.tour_status === 'Previously Toured';
    if (filter === 'Tour Scheduled') return g.relationship?.tour_status === 'Tour Scheduled';
    if (filter === 'Fillout') return g.rsvp_source?.includes('Fillout');
    if (filter === 'Eventbrite') return g.rsvp_source?.includes('Eventbrite');
    
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-cinzel text-padua-green">Guest Directory</h1>
        
        {guests.length > 0 && (
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search guests..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-padua-gold bg-white"
              />
            </div>
            <select 
              value={filter} 
              onChange={e => setFilter(e.target.value)}
              className="border rounded-lg px-4 py-2 bg-white focus:ring-padua-gold"
            >
              <option value="All">All Guests</option>
              <option value="Checked In">Checked In</option>
              <option value="Not Arrived">Not Arrived</option>
              <option value="Booked">Booked Couples</option>
              <option value="Prospective">Prospective Couples</option>
              <option value="Never Toured">Never Toured</option>
              <option value="Previously Toured">Previously Toured</option>
              <option value="Tour Scheduled">Tour Scheduled</option>
              <option value="Fillout">Fillout</option>
              <option value="Eventbrite">Eventbrite</option>
              <option value="Walk-Ins">Walk-Ins</option>
            </select>
          </div>
        )}
      </div>

      {guests.length === 0 && !search && filter === 'All' ? (
        <Card className="p-12 text-center flex flex-col items-center justify-center border-dashed">
          <FiUsers className="text-5xl mb-4 text-padua-sage opacity-50" />
          <h3 className="text-2xl font-cinzel text-padua-green mb-2">No local guests</h3>
          <p className="text-gray-500 mb-8 max-w-md">Import a CSV file to this device to populate the directory.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={() => navigate('/admin/imports')} variant="primary" className="px-8">
              Import Guest List
            </Button>
            <Button onClick={() => navigate('/walk-in')} variant="outline" className="px-8">
              Add Walk-In
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-500 uppercase tracking-wider">
                <th className="p-4">Name</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Arrival Status</th>
                <th className="p-4">Relationship</th>
                <th className="p-4">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredGuests.map(g => (
                <tr key={g.registration_id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <div className="font-medium text-gray-900">{g.contact.first_name} {g.contact.last_name}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-gray-900">{g.contact.email}</div>
                    <div className="text-xs text-gray-500">{g.contact.phone}</div>
                  </td>
                  <td className="p-4">
                    {g.checked_in ? (
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Checked In
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {g.check_in_time && format(new Date(g.check_in_time), 'h:mm a')}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Not Arrived
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-gray-900">{g.relationship?.relationship_type || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{g.relationship?.tour_status}</div>
                  </td>
                  <td className="p-4 text-sm text-gray-500">
                    {g.rsvp_source}
                    {g.walk_in && <span className="ml-2 text-xs text-purple-600 font-medium border border-purple-200 px-1 rounded bg-purple-50">Walk-In</span>}
                  </td>
                </tr>
              ))}
              {filteredGuests.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">No guests found matching criteria.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};